/* Carousel affordance.
   The rows already scrolled, but nothing on screen said so. This wraps each
   product row in a rail, adds a progress bar that tracks the scroll, and
   nudges once on first sight so the motion itself announces the gesture.
   Everything degrades to a plain scrolling row if JS never runs. */
(() => {
  const MOBILE = () => window.matchMedia('(max-width: 900px)').matches;

  function enhance(row) {
    if (row.dataset.crslReady) return;
    row.dataset.crslReady = '1';

    // Wrap so the edge fade has something to attach to.
    const rail = document.createElement('div');
    rail.className = 'collection-showcase__rail';
    row.parentNode.insertBefore(rail, row);
    rail.appendChild(row);

    const bar = document.createElement('div');
    bar.className = 'crsl-bar';
    const fill = document.createElement('span');
    fill.className = 'crsl-bar__fill';
    bar.appendChild(fill);
    rail.after(bar);

    const paint = () => {
      const max = row.scrollWidth - row.clientWidth;
      if (max <= 4) { bar.style.display = 'none'; return; }
      bar.style.display = '';
      // Width of the thumb reflects how much of the row is on screen.
      const frac = row.clientWidth / row.scrollWidth;
      const pos = row.scrollLeft / max;
      fill.style.width = (frac * 100) + '%';
      fill.style.transform = 'translateX(' + (pos * (100 / frac - 100)) + '%)';
    };
    paint();
    // A plain scroll listener proved unreliable here: the handler was bound and
    // the row scrolled, but the thumb never repainted. Driving it from a frame
    // loop while the row is in view removes the dependency on that event
    // firing at all, and costs nothing when the row is off screen.
    let ticking = false;
    let lastLeft = -1;
    const tick = () => {
      if (!ticking) return;
      if (row.scrollLeft !== lastLeft) { lastLeft = row.scrollLeft; paint(); }
      requestAnimationFrame(tick);
    };
    const vis = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting && !ticking) { ticking = true; requestAnimationFrame(tick); }
        else if (!e.isIntersecting) { ticking = false; }
      });
    }, { threshold: 0 });
    vis.observe(row);

    row.addEventListener('scroll', paint, { passive: true });
    window.addEventListener('resize', paint);

    // One nudge the first time a row comes into view: a static peek is easy to
    // read as a cropping bug, movement is unambiguous.
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (!e.isIntersecting || row.dataset.crslNudged) return;
          row.dataset.crslNudged = '1';
          if (row.scrollWidth - row.clientWidth < 40) return;
          row.scrollTo({ left: 34, behavior: 'smooth' });
          setTimeout(() => row.scrollTo({ left: 0, behavior: 'smooth' }), 520);
        });
      }, { threshold: .35 });
      io.observe(row);
    }
  }

  function init() {
    if (!MOBILE()) return;
    document.querySelectorAll('.collection-showcase .card-row').forEach(enhance);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  window.addEventListener('resize', () => { if (MOBILE()) init(); });
})();

/* Tabbed rail: switch panels. Panels are rendered server-side so every tab's
   products are in the HTML and indexable; this only toggles which is shown. */
(() => {
  function init(root) {
    const tabs = [...root.querySelectorAll('[data-trail-tab]')];
    const panels = [...root.querySelectorAll('[data-trail-panel]')];
    if (!tabs.length) return;
    tabs.forEach(tab => {
      tab.addEventListener('click', () => {
        const id = tab.dataset.trailTab;
        tabs.forEach(t => {
          const on = t === tab;
          t.classList.toggle('is-active', on);
          t.setAttribute('aria-selected', String(on));
        });
        panels.forEach(p => {
          const on = p.dataset.trailPanel === id;
          p.classList.toggle('is-active', on);
          p.hidden = !on;
        });
      });
    });
  }
  const run = () => document.querySelectorAll('[data-trail]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
})();

/* Reveal on scroll. Marks every top-level band and fades it up once as it
   arrives. Opt-out is automatic under reduced motion, and anything already on
   screen at load is shown immediately so the first paint is never blank. */
(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  const bands = [...document.querySelectorAll(
    '.shopify-section > section, .shopify-section > .trail, .shopify-section > .pills'
  )].filter(el => !el.closest('.hero') && !el.matches('.hero')
    // A transformed ancestor becomes the containing block for any fixed
    // descendant, which is what anchored the cart's pay bar to the section
    // instead of the viewport.
    && !el.matches('.cart-page') && !el.closest('.cart-page'));
  if (!bands.length) return;

  const io = new IntersectionObserver((entries) => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      e.target.classList.add('is-in');
      io.unobserve(e.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.04 });

  bands.forEach(el => {
    // Already in view on load: show it, do not animate the first screen.
    if (el.getBoundingClientRect().top < window.innerHeight * 0.92) {
      el.setAttribute('data-reveal', '');
      el.classList.add('is-in');
      return;
    }
    el.setAttribute('data-reveal', '');
    io.observe(el);
  });
})();

/* Quick-add confirmation.

   The previous version appended a tick element and set the button to
   position:relative. The button is absolutely positioned inside the card, so
   that override dropped it to the card's top-left corner the moment it was
   clicked. The confirmation is a class now; CSS draws the tick with a
   pseudo-element and nothing about layout is touched. */
(() => {
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('.product-card__quick-add');
    if (!btn) return;
    btn.classList.add('is-added');
    setTimeout(() => btn.classList.remove('is-added'), 1300);
  }, true);
})();

/* Header compaction.
   The bar stays put, it just gets shorter once scrolling starts.

   This used to also hide the header magnifier whenever the full field was on
   screen. That handoff ran on load, so the icon appeared or vanished a moment
   after first paint depending on where the page happened to be scrolled, and
   the magnifier beside the field was never a problem to begin with. It stays
   put now. */
(() => {
  const header = document.querySelector('.site-header');
  if (!header) return;
  const onScroll = () => header.classList.toggle('is-compact', window.scrollY > 40);
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
})();

/* Search field.
   The field is page content and scrolls away on its own, so the collapse this
   block used to drive is redundant. It was also actively harmful: it ran once
   on load, so a page restored to a scroll position past 150px painted with the
   field folded shut and then sprang it open. Nothing to do here but leave the
   markup alone. */

/* Category artwork: warm and fade.

   Panels that are hidden defer their image because of loading="lazy", so the
   first click on a tab showed the flat tint for as long as the fetch took.
   Every banner is warmed once the page is idle, and each fades in rather than
   snapping, so even a cold one arrives deliberately. */
(() => {
  const imgs = [...document.querySelectorAll('.trail__art-img')];
  if (!imgs.length) return;

  const mark = (img) => img.classList.add('is-loaded');
  imgs.forEach(img => {
    if (img.complete && img.naturalWidth > 0) mark(img);
    else img.addEventListener('load', () => mark(img), { once: true });
    img.addEventListener('error', () => { img.style.display = 'none'; }, { once: true });
  });

  // Pull the hidden ones into cache without competing with the first paint.
  const warm = () => imgs.forEach(img => {
    const src = img.currentSrc || img.getAttribute('src');
    if (!src || (img.complete && img.naturalWidth > 0)) return;
    img.loading = 'eager';
    const pre = new Image();
    pre.decoding = 'async';
    pre.src = src;
  });

  if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 2500 });
  else setTimeout(warm, 1200);
})();

/* Collection hero rotator.
   Cross-fades the featured product every few seconds. Pauses while the pointer
   is over it and while the tab is hidden, so it is never animating at someone
   who is reading it or burning cycles off screen. */
(() => {
  const host = document.querySelector('[data-chero-rotator]');
  if (!host) return;
  const slides = [...host.querySelectorAll('[data-chero-slide]')];
  if (slides.length < 2) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  let i = 0;
  let paused = false;
  const show = (n) => slides.forEach((s, k) => {
    const on = k === n;
    s.classList.toggle('is-on', on);
    if (on) s.removeAttribute('aria-hidden'); else s.setAttribute('aria-hidden', 'true');
  });

  host.addEventListener('pointerenter', () => { paused = true; });
  host.addEventListener('pointerleave', () => { paused = false; });
  host.addEventListener('focusin', () => { paused = true; });
  host.addEventListener('focusout', () => { paused = false; });

  setInterval(() => {
    if (paused || document.hidden) return;
    i = (i + 1) % slides.length;
    show(i);
  }, 4200);
})();
